import {
  AloftPrice,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectOrderHasFunctionalDepreciation } from '../selectOrderDepreciation.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { priceJSONToPrice } from '../utils/priceJSONToPrice.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateFunctionalUtilityAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  orderHasFunctionalDepreciation: boolean,
  compID: string,
  salesComparisonApproach: SalesComparisonApproachType,
) => {
  // No unit-rate adjustments for functional utility
  if (salesComparisonApproach === 'unit_rate') return undefined;

  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];
  if (!orderHasFunctionalDepreciation || !subjectImprovements || !compImprovements) {
    return undefined;
  }

  const { functionalDepreciation } = subjectImprovements;
  const { functionalDepreciation: compFunctionalDepreciation } = compImprovements;

  if (!functionalDepreciation && !compFunctionalDepreciation) return undefined;

  const functionalDepreciationParsed = priceJSONToPrice(functionalDepreciation) ?? AloftPrice.zero;
  const compFunctionalDepreciationParsed =
    priceJSONToPrice(compFunctionalDepreciation) ?? AloftPrice.zero;

  // Comp - Subject, since the depreciation values are stored as positive values
  return compFunctionalDepreciationParsed.sub(functionalDepreciationParsed);
};

const selectFunctionalUtilityAdj = createPriceSelector(
  [
    selectImprovementsEntities,
    selectOrderHasFunctionalDepreciation,
    (_state, id: string) => id,
    selectSalesComparisonApproach,
  ],
  calculateFunctionalUtilityAdj,
);

export const selectFunctionalUtilityAdjs = createSelector(
  [
    selectImprovementsEntities,
    selectOrderHasFunctionalDepreciation,
    selectCompSequence,
    selectSalesComparisonApproach,
  ],
  (improvements, orderHasFunctionalDepreciation, compSequence, salesComparisonApproach) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateFunctionalUtilityAdj(
        improvements,
        orderHasFunctionalDepreciation,
        propID,
        salesComparisonApproach,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedFunctionalUtilityAdj = createSelector(
  [selectFunctionalUtilityAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'functionalUtility', adjSettings),
);

export const selectAppliedFunctionalUtilityAdjDisplay = createSelector(
  [selectAppliedFunctionalUtilityAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedFunctionalUtilityAdjsDisplay = createSelector(
  [selectCompSequence, selectFunctionalUtilityAdjs, selectAdjustmentSettings],
  (compSequence, conditionAdjs, adjSettings) =>
    compSequence.reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = conditionAdjs[propID];

      if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'functionalUtility', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
